/**
 * Created by Ning Jiang on 2021-06-22.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {gsap, TimelineLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {BigWinView} from "./BigWinView";
import {DeadwoodRip} from "../../../DeadwoodRip";
import {DeadwoodRipGameConfig} from "../../../DeadwoodRipGameConfig";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {DeadwoodRipSound} from "../../../sound/DeadwoodRipSound";

export class BigWinController extends WinPresentationController {

    private _countUpEndSoundDuration:number[] = [6.6, 6.6, 6.76, 7.0, 6.6, 6.6];
    private _view:BigWinView;
    private _levelTimeline:TimelineLite | null;
    private _levelSkipped:boolean;

    constructor() {
        super("BigWinController", true);
        this._view = new BigWinView();
    }

    protected hasWin():boolean {
        return DeadwoodRip.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1;
    }

    protected async createWinAnimation():Promise<any> {
        DeadwoodRip.playScattersWin = true;
        await new Promise<any>(resolve => {
            DeadwoodRipSound.fadeMusic(0, 200);
            this.playBigWinLevel(0, resolve);
        });
    }

    private playBigWinLevel(level:number, resolve:(value:any) => void):void {
        const data = DeadwoodRip.model.parsedGameData;
        if(level > data.currentWinRatioConfig!.bigWinLevel) {
            this._levelTimeline = null;
            this.playEnding(data.currentWinRatioConfig!.bigWinLevel, resolve);
        } else {
            const isEndLevel = level === data.currentWinRatioConfig!.bigWinLevel;
            const currentRatio = DeadwoodRipGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === level)!;
            const from:number = level === 0 ? 0 : data.playedBetValue * currentRatio.ratio;
            const to:number = isEndLevel ? data.singleWin : data.playedBetValue * DeadwoodRipGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === (level + 1))!.ratio;
            const tooSmallEndLevel:boolean = isEndLevel && this.isLevelWinTooSmall(level, from, to);
            const tl = new TimelineLite();
            tl.add(this.getLevelAnimation(level, currentRatio.duration, from, to, isEndLevel));
            tl.add(() => this.playBigWinLevel(level + 1, resolve));
            this._levelTimeline = tl;
            if(tooSmallEndLevel) {
                this._levelTimeline.progress(1);
            } else {
                this._flowStateController.setSkipCallback(() => this.skip!());
                DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.BIG_WIN[level]});
                DeadwoodRipSound.soundPlayer.playTrack({
                    name : DeadwoodRipSound.BIG_WIN_COUNT_UP,
                    volume : level > 2 ? 1 : 0.7
                });
            }
        }
    }

    private isLevelWinTooSmall(level:number, from:number, to:number):boolean {
        if(level === 0) {
            return false;
        }

        return (to - from) <= (from * 0.1);
    }

    private getLevelAnimation(level:number, duration:number, from:number, to:number, isEndLevel:boolean):TimelineLite {
        DeadwoodRip.bigWinCountUp.onEnd(() => {
            if(!this._levelSkipped) {
                DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.BIG_WIN_COUNT_UP_ENDS});
            }
        });
        const tl = new TimelineLite();
        tl.add([
            () => {
                this._levelSkipped = false;
                DeadwoodRip.bigWinCountUp.scale.set(1.2);
            },
            DeadwoodRip.bigWinCountUp.getCountUpAnimation(from, to, duration),
            gsap.to(DeadwoodRip.bigWinCountUp.scale, {duration : duration, x : 1, y : 1, ease : `power4.out`}),
            this._view.getLevelAnimation(level, duration, isEndLevel)
        ]);
        return tl;
    }

    protected skip() {
        this._levelSkipped = true;
        DeadwoodRipSound.BIG_WIN.forEach(name => {
            DeadwoodRipSound.soundPlayer.stopTrack(name);
        });
        DeadwoodRipSound.soundPlayer.stopTrack(DeadwoodRipSound.BIG_WIN_INTRO);
        DeadwoodRipSound.soundPlayer.stopTrack(DeadwoodRipSound.BIG_WIN_COUNT_UP);
        if(this._levelTimeline && this._levelTimeline.isActive()) {
            this._levelTimeline.progress(1);
        }
    }

    private playEnding(endLevel:number, resolve:(value:any) => void):void {
        this._flowStateController.setSkipCallback();
        DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.BIG_WIN_END});
        if(!DeadwoodRip.model.parsedGameData.brokeTheBank) {
            DeadwoodRipSound.fadeMusic(1, 200);
        }
        DeadwoodRip.bigWinCountUp.stay();
        DeadwoodRip.waysWon.stay();
        SlotGame.keypadPlugin.setWin(DeadwoodRip.model.parsedGameData.totalWin, DeadwoodRip.model.parsedGameData.showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
        this._view.playEnding(endLevel);
        const tl = new TimelineLite();
        tl.add(() => {
            resolve(this);
        }, DeadwoodRip.model.parsedGameData.brokeTheBank ? 0 : 0.9);
    }
}